import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Coins, ShieldCheck, LogOut, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar({ onBuy }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const total = (user?.credits ?? 0) + (user?.sub_credits ?? 0);

  return (
    <header className="fixed top-0 inset-x-0 z-50 h-16 border-b border-[#243047] bg-[#0A0D14]/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto h-full px-4 md:px-6 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="navbar-brand-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#EC1E9C] to-[#00FFC2] flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#03110B]" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-extrabold text-white text-lg tracking-tight">{t("brand")}</span>
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          <div data-testid="navbar-signals-credit-counter"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] bg-[#121824]"
            title={user?.subscription ? t("nav.subCredits", { n: user.sub_credits, m: user.credits }) : undefined}>
            <Coins className="w-4 h-4 text-[#EC1E9C]" />
            <span className="font-mono font-bold text-white text-sm" data-testid="navbar-total-credits">{total}</span>
            <span className="text-[11px] text-[#64748B] hidden sm:inline">{t("nav.credits")}</span>
            {user?.subscription && (
              <span className="hidden md:inline text-[10px] font-mono text-[#00FFC2] ml-1" data-testid="navbar-credits-breakdown">
                ({t("nav.subCredits", { n: user.sub_credits, m: user.credits })})
              </span>
            )}
          </div>

          <button onClick={onBuy} data-testid="navbar-buy-credits-button"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold text-sm hover:brightness-110 transition">
            <Coins className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.buy")}</span>
          </button>

          {user?.role === "admin" && (
            <Link to="/admin" data-testid="navbar-admin-panel-link"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] text-[#94A3B8] hover:text-white text-sm transition">
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.admin")}</span>
            </Link>
          )}

          <LanguageSwitcher />

          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="navbar-logout"
            className="p-2 rounded-lg border border-[#243047] text-[#94A3B8] hover:text-[#EC1E9C] transition" title={t("nav.logout")}>
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
